import React from 'react';
import StandaloneSection from './shared/StandaloneSection';
import './PauseInformationPanel.css';

interface PauseInformationPanelProps {
  /**
   * When true, render the OPERATOR_PAUSED variant (REQ-013): the group was manually
   * halted by an operator and will NOT auto-resume — it must be resumed manually. This
   * is distinct from the automated-pause copy ("will be automatically resumed"). The
   * reason/time may be NULL for non-RSI groups, so the variant never depends on them.
   */
  operatorPaused?: boolean;
  pauseTime?: number | null;
  pauseReason?: string | null;
  pauseCount: number;
  peakCumulativePnl?: number | null;
  currentCumulativePnl?: number;
  // Optional metrics that triggered the pause
  winRate?: number | null;
  dollarLoss?: number | null;
  drawdownPct?: number | null;
  consecutiveLosses?: number | null;
  // Resume eligibility status (will be populated by backend in future)
  minPauseDurationMet?: boolean;
  marketRegimeFavorable?: boolean;
  volatilityPercentile?: number | null;
  trendStrength?: number | null;
}

/**
 * Format relative time from timestamp
 */
const formatRelativeTime = (timestamp: number): string => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m ago`;
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h ago`;
};

/**
 * Format timestamp to readable date/time
 */
const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format currency
 */
const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '$0.00';
  const decimalPlaces = Math.abs(value) < 1 ? 4 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value);
};

/**
 * PauseInformationPanel Component
 * Displays detailed pause information and resume eligibility status
 * Used in TradeGroupDetail view when trade group is in PAUSED state
 */
const PauseInformationPanel: React.FC<PauseInformationPanelProps> = ({
  operatorPaused = false,
  pauseTime,
  pauseReason,
  pauseCount,
  peakCumulativePnl,
  currentCumulativePnl,
  winRate,
  dollarLoss,
  drawdownPct,
  consecutiveLosses,
  minPauseDurationMet,
  marketRegimeFavorable,
  volatilityPercentile,
  trendStrength,
}) => {
  // OPERATOR_PAUSED variant (REQ-013): distinct copy that does NOT promise auto-resume,
  // and never depends on a reason/time being present (NULL for non-RSI groups).
  if (operatorPaused) {
    const hasPauseTime = pauseTime != null;
    const hasReason = pauseReason != null && pauseReason.trim().length > 0;
    return (
      <div className="pause-information-panel">
        <StandaloneSection title="Operator Pause Information" className="pause-info-section">
          <div className="pause-details">
            <div className="pause-status-header">
              <div className="pause-icon">⏸️</div>
              <div className="pause-status-text">
                <h3>Operator-Paused</h3>
                {hasPauseTime && (
                  <p className="pause-time">
                    {formatRelativeTime(pauseTime!)} ({formatTimestamp(pauseTime!)})
                  </p>
                )}
              </div>
            </div>

            <div className="resume-eligibility-section">
              <div className="eligibility-pending">
                <span className="eligibility-icon">🔒</span>
                <p>This group was manually paused by an operator.</p>
                <p className="eligibility-sub">
                  An operator-paused group opens no new positions and will <strong>NOT</strong>{' '}
                  auto-resume — it remains parked until an operator manually resumes it. Any open
                  in-flight position continues to be managed to completion.
                </p>
              </div>
            </div>

            {hasReason && (
              <div className="pause-reason-box">
                <label>Operator Note:</label>
                <p className="pause-reason-text">{pauseReason}</p>
              </div>
            )}

            <div className="pause-metrics-grid">
              <div className="pause-metric">
                <span className="metric-label">Total Pauses</span>
                <span className="metric-value">{pauseCount}</span>
              </div>
            </div>
          </div>
        </StandaloneSection>
      </div>
    );
  }

  // Automated-pause path (unchanged): pauseTime is always present here.
  const safePauseTime = pauseTime ?? Date.now();
  // Calculate pause duration
  const pauseDuration = Date.now() - safePauseTime;
  const pauseDurationHours = Math.floor(pauseDuration / 3600000);
  const pauseDurationMinutes = Math.floor((pauseDuration % 3600000) / 60000);

  // Calculate drawdown if we have both values
  const calculatedDrawdown = peakCumulativePnl && currentCumulativePnl != null && peakCumulativePnl > 0
    ? ((peakCumulativePnl - currentCumulativePnl) / peakCumulativePnl) * 100
    : null;

  return (
    <div className="pause-information-panel">
      <StandaloneSection title="Pause Information" className="pause-info-section">
        <div className="pause-details">
          {/* Pause Status Header */}
          <div className="pause-status-header">
            <div className="pause-icon">⏸️</div>
            <div className="pause-status-text">
              <h3>Trade Group Paused</h3>
              <p className="pause-time">{formatRelativeTime(safePauseTime)} ({formatTimestamp(safePauseTime)})</p>
            </div>
          </div>

          {/* Pause Reason */}
          <div className="pause-reason-box">
            <label>Pause Reason:</label>
            <p className="pause-reason-text">{pauseReason ?? 'No reason provided'}</p>
          </div>

          {/* Pause Metrics Grid */}
          <div className="pause-metrics-grid">
            <div className="pause-metric">
              <span className="metric-label">Pause Duration</span>
              <span className="metric-value">
                {pauseDurationHours}h {pauseDurationMinutes}m
              </span>
            </div>

            <div className="pause-metric">
              <span className="metric-label">Total Pauses</span>
              <span className="metric-value">{pauseCount}</span>
            </div>

            {winRate != null && (
              <div className="pause-metric">
                <span className="metric-label">Win Rate at Pause</span>
                <span className={`metric-value ${winRate < 0.45 ? 'negative' : ''}`}>
                  {(winRate * 100).toFixed(1)}%
                </span>
              </div>
            )}

            {consecutiveLosses != null && consecutiveLosses > 0 && (
              <div className="pause-metric">
                <span className="metric-label">Consecutive Losses</span>
                <span className="metric-value negative">{consecutiveLosses}</span>
              </div>
            )}

            {dollarLoss != null && dollarLoss < 0 && (
              <div className="pause-metric">
                <span className="metric-label">Dollar Loss (24h)</span>
                <span className="metric-value negative">{formatCurrency(Math.abs(dollarLoss))}</span>
              </div>
            )}

            {(drawdownPct != null || calculatedDrawdown != null) && (
              <div className="pause-metric">
                <span className="metric-label">Drawdown from Peak</span>
                <span className="metric-value negative">
                  {(drawdownPct || calculatedDrawdown)!.toFixed(2)}%
                </span>
                {peakCumulativePnl != null && (
                  <span className="metric-sub">Peak: {formatCurrency(peakCumulativePnl)}</span>
                )}
              </div>
            )}
          </div>

          {/* Resume Eligibility Section */}
          <div className="resume-eligibility-section">
            <h4>Resume Eligibility Status</h4>

            {/* Check if resume eligibility data is available */}
            {minPauseDurationMet === undefined ? (
              <div className="eligibility-pending">
                <span className="eligibility-icon">⏳</span>
                <p>Resume eligibility evaluation in progress</p>
                <p className="eligibility-sub">
                  The system automatically evaluates paused trade groups every minute. This trade group will be automatically resumed when the minimum pause duration has elapsed and market conditions are favorable (sideways/low volatility, weak trend).
                </p>
              </div>
            ) : (
              <div className="eligibility-checks">
                {/* Minimum Pause Duration Check */}
                <div className={`eligibility-check ${minPauseDurationMet ? 'pass' : 'fail'}`}>
                  <span className="check-icon">{minPauseDurationMet ? '✓' : '○'}</span>
                  <div className="check-details">
                    <span className="check-label">Minimum Pause Duration</span>
                    <span className="check-status">
                      {minPauseDurationMet ? 'Met' : `${Math.max(0, 60 - pauseDurationMinutes)}m remaining`}
                    </span>
                  </div>
                </div>

                {/* Market Conditions Check */}
                <div className={`eligibility-check ${marketRegimeFavorable ? 'pass' : 'fail'}`}>
                  <span className="check-icon">{marketRegimeFavorable ? '✓' : '✗'}</span>
                  <div className="check-details">
                    <span className="check-label">Market Regime</span>
                    <span className="check-status">
                      {marketRegimeFavorable ? 'Favorable (Sideways/Low Volatility)' : 'Unfavorable (Trending/High Volatility)'}
                    </span>
                  </div>
                </div>

                {/* Volatility Percentile Check */}
                {volatilityPercentile != null && (
                  <div className={`eligibility-check ${volatilityPercentile <= 0.7 ? 'pass' : 'fail'}`}>
                    <span className="check-icon">{volatilityPercentile <= 0.7 ? '✓' : '✗'}</span>
                    <div className="check-details">
                      <span className="check-label">Volatility Percentile</span>
                      <span className="check-status">
                        {(volatilityPercentile * 100).toFixed(0)}% {volatilityPercentile <= 0.7 ? '≤ 70%' : '> 70%'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Trend Strength Check */}
                {trendStrength != null && (
                  <div className={`eligibility-check ${trendStrength <= 0.3 ? 'pass' : 'fail'}`}>
                    <span className="check-icon">{trendStrength <= 0.3 ? '✓' : '✗'}</span>
                    <div className="check-details">
                      <span className="check-label">Trend Strength</span>
                      <span className="check-status">
                        {trendStrength.toFixed(2)} {trendStrength <= 0.3 ? '≤ 0.30' : '> 0.30'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Overall Status */}
                <div className="eligibility-summary">
                  {minPauseDurationMet && marketRegimeFavorable &&
                   (volatilityPercentile == null || volatilityPercentile <= 0.7) &&
                   (trendStrength == null || trendStrength <= 0.3) ? (
                    <div className="eligibility-ready">
                      <span className="summary-icon">✓</span>
                      <span className="summary-text">Eligible for resume - awaiting next evaluation cycle</span>
                    </div>
                  ) : !minPauseDurationMet ? (
                    <div className="eligibility-waiting">
                      <span className="summary-icon">⏳</span>
                      <span className="summary-text">
                        Resume eligibility check in {Math.max(0, 60 - pauseDurationMinutes)} minutes (minimum pause duration)
                      </span>
                    </div>
                  ) : (
                    <div className="eligibility-blocked">
                      <span className="summary-icon">✗</span>
                      <span className="summary-text">Not eligible - market conditions unfavorable</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </StandaloneSection>
    </div>
  );
};

export default PauseInformationPanel;
